/**
 * P0 Workflow Integrity — frontend consumer of the canonical
 * source of truth. Fetches `/api/workflow/canonical` once and
 * exposes:
 *   • order           : canonical stage list from backend/workflow.py
 *   • labels          : {stageKey -> humanLabel}
 *   • prereq          : {stageKey -> upstreamStageKey}
 *   • satisfiedStates : states that count a stage as prereq-satisfied
 *   • computeNextStage(project) — mirror of backend compute_next_stage
 *   • isPrereqSatisfied(project, stageKey) — mirror of backend rule
 *
 * The React `useCanonicalWorkflow()` hook returns
 * `{ ready, order, labels, prereq, computeNextStage, isPrereqSatisfied }`.
 *
 * The frontend NEVER hard-codes the stage order or prereq graph.
 * If `/api/workflow/canonical` fails, the hook falls back to a
 * conservative default that MATCHES backend/workflow.py — but this
 * fallback is only for the first paint before the fetch resolves.
 */
import { useEffect, useState } from "react";
import api from "./api";

const _FALLBACK = {
    order: [
        "ceo_briefing", "asset_upload", "competitor_analysis",
        "brand_dna", "research", "business_plan", "concepts",
        "script", "storyboard", "presentation", "prompts",
        "qc_review", "post_production", "final_delivery",
    ],
    labels: {
        ceo_briefing: "Project Brief",
        asset_upload: "Asset Upload",
        competitor_analysis: "Competitor Research",
        brand_dna: "Brand DNA",
        research: "Market Research",
        business_plan: "Business Plan",
        concepts: "Concepts",
        script: "Script",
        storyboard: "Storyboard",
        presentation: "Presentation",
        prompts: "Prompt Pack",
        qc_review: "QC Review",
        post_production: "Post-Production",
        final_delivery: "Final Delivery",
    },
    prereq: {
        competitor_analysis: "ceo_briefing",
        brand_dna: "competitor_analysis",
        research: "brand_dna",
        business_plan: "research",
        concepts: "business_plan",
        script: "concepts",
        storyboard: "script",
        presentation: "storyboard",
        prompts: "storyboard",
        qc_review: "prompts",
        post_production: "storyboard",
        final_delivery: "post_production",
    },
    satisfied_states: ["approved", "manually_completed"],
    skipped_counts_when_reason_set: true,
    source_of_truth: "backend/workflow.py::CANONICAL_ORDER",
};

// Global module cache — one fetch per page load, shared across every
// component that mounts the hook.
let _CACHE = null;
let _PENDING = null;

async function _fetchCanonical() {
    if (_CACHE) return _CACHE;
    if (_PENDING) return _PENDING;
    _PENDING = (async () => {
        try {
            const r = await api.get("/workflow/canonical");
            const m = r?.data || {};
            _CACHE = {
                order: Array.isArray(m.order) && m.order.length ? m.order : _FALLBACK.order,
                labels: m.labels && typeof m.labels === "object" ? m.labels : _FALLBACK.labels,
                prereq: m.prereq && typeof m.prereq === "object" ? m.prereq : _FALLBACK.prereq,
                satisfiedStates: Array.isArray(m.satisfied_states) && m.satisfied_states.length
                    ? m.satisfied_states : _FALLBACK.satisfied_states,
                skippedCountsWhenReasonSet: m.skipped_counts_when_reason_set !== false,
                source: m.source_of_truth || _FALLBACK.source_of_truth,
            };
            return _CACHE;
        } catch {
            _CACHE = {
                order: _FALLBACK.order,
                labels: _FALLBACK.labels,
                prereq: _FALLBACK.prereq,
                satisfiedStates: _FALLBACK.satisfied_states,
                skippedCountsWhenReasonSet: true,
                source: _FALLBACK.source_of_truth,
            };
            return _CACHE;
        } finally {
            _PENDING = null;
        }
    })();
    return _PENDING;
}

/** Pure helper: is `stageKey`'s prereq satisfied on `project`? */
export function isPrereqSatisfied(project, stageKey, canonical) {
    const wf = canonical || _CACHE || _FALLBACK;
    const prereqMap = wf.prereq || wf.prereq;
    const prev = prereqMap[stageKey];
    if (!prev) return { ok: true, reason: "no_prereq" };
    const stages = (project && project.stages) || {};
    const row = stages[prev] || {};
    const status = String(row.status || "").toLowerCase();
    const satisfied = (wf.satisfiedStates || wf.satisfied_states || ["approved", "manually_completed"])
        .map((s) => String(s).toLowerCase());
    if (satisfied.includes(status)) return { ok: true, reason: prev };
    if (status === "skipped" && String(row.skip_reason || "").trim() &&
        (wf.skippedCountsWhenReasonSet !== false)) {
        return { ok: true, reason: `${prev}:skipped_with_reason` };
    }
    const label = (wf.labels || {})[prev] || prev;
    const stageLabel = (wf.labels || {})[stageKey] || stageKey;
    return {
        ok: false,
        reason: `Cannot run ${stageLabel}: ${label} must be approved first (current: ${status || "idle"}).`,
        prereqStage: prev,
        prereqLabel: label,
        prereqStatus: status || "idle",
    };
}

/** Pure helper: mirror of backend `compute_next_stage(project)`. */
export function computeNextStage(project, canonical) {
    const wf = canonical || _CACHE || _FALLBACK;
    const order = wf.order || _FALLBACK.order;
    const stages = (project && project.stages) || {};
    const satisfied = (wf.satisfiedStates || wf.satisfied_states || ["approved", "manually_completed"])
        .map((s) => String(s).toLowerCase());
    for (const sk of order) {
        const row = stages[sk] || {};
        const status = String(row.status || "").toLowerCase();
        if (satisfied.includes(status)) continue;
        if (status === "skipped" && String(row.skip_reason || "").trim()) continue;
        return sk;
    }
    return null;
}

/** React hook — resolves canonical workflow from backend once. */
export function useCanonicalWorkflow() {
    const [state, setState] = useState(() => _CACHE || null);
    useEffect(() => {
        if (_CACHE) { setState(_CACHE); return; }
        let alive = true;
        _fetchCanonical().then((wf) => {
            if (alive) setState(wf);
        });
        return () => { alive = false; };
    }, []);
    const wf = state || {
        order: _FALLBACK.order,
        labels: _FALLBACK.labels,
        prereq: _FALLBACK.prereq,
        satisfiedStates: _FALLBACK.satisfied_states,
        skippedCountsWhenReasonSet: true,
        source: _FALLBACK.source_of_truth,
    };
    return {
        ready: !!state,
        order: wf.order,
        labels: wf.labels,
        prereq: wf.prereq,
        satisfiedStates: wf.satisfiedStates,
        source: wf.source,
        computeNextStage: (project) => computeNextStage(project, wf),
        isPrereqSatisfied: (project, stageKey) => isPrereqSatisfied(project, stageKey, wf),
    };
}
